/**
 * Reusable on-screen keyboard for controller-first text entry (Jonathan
 * 2026-05-21). The study rig is driven from a gamepad in the passenger seat,
 * so any free-text field (player profile name, authored-asset name, …) needs
 * a way to type without a physical keyboard.
 *
 * Fully self-contained: it builds its own dim + panel overlay at a high depth,
 * registers its own gamepad polling (on the scene UPDATE event) and a DOM
 * keydown listener (so a developer on a laptop can still just type), and calls
 * back `onSubmit(value)` / `onCancel()`. The host scene should stop driving its
 * own input while a keyboard is open (check `isOpen()` / hold a reference).
 *
 * Controls:
 *  - D-pad / left-stick / arrows / WASD — move the key cursor.
 *  - A / Space / Enter — type the focused key (or activate SPACE / DEL / OK).
 *  - B — backspace.   Start — confirm (OK).   Esc — cancel.
 *  - Physical keys a–z 0–9 space _ - . ' type directly; Backspace / Enter /
 *    Escape map to DEL / OK / cancel.
 */

import Phaser from "phaser";

import { NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";

type KeyKind = "char" | "space" | "del" | "ok";

interface KbKey {
  label: string;
  kind: KeyKind;
  /** Inserted value for `char` keys. */
  value?: string;
}

const CHAR_ROWS: readonly string[] = [
  "abcdefghij",
  "klmnopqrst",
  "uvwxyz0123",
  "456789_-.'",
];

/** Characters accepted from the physical keyboard. */
const ALLOWED_CHAR = /^[a-z0-9 _\-.']$/;

const PANEL_W = 320;
const PANEL_H = 150;
const CELL = 26;
const CELL_GAP = 2;

export interface OnScreenKeyboardOpts {
  title: string;
  initial?: string;
  maxLen?: number;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

export class OnScreenKeyboard {
  private readonly scene: Phaser.Scene;
  private readonly opts: OnScreenKeyboardOpts;
  private readonly maxLen: number;

  private value: string;
  private readonly grid: KbKey[][] = [];
  private readonly cellObjs: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[][] = [];
  private focusRow = 0;
  private focusCol = 0;

  private readonly container: Phaser.GameObjects.Container;
  private readonly valueText: Phaser.GameObjects.Text;
  private caretVisible = true;
  private caretTimer?: Phaser.Time.TimerEvent;

  private readonly keydownHandler: (ev: KeyboardEvent) => void;
  private prevDx = 0;
  private prevDy = 0;
  private readonly prevButtons: Record<string, boolean> = {};
  private open = true;

  constructor(scene: Phaser.Scene, opts: OnScreenKeyboardOpts) {
    this.scene = scene;
    this.opts = opts;
    this.maxLen = opts.maxLen ?? 16;
    this.value = (opts.initial ?? "").slice(0, this.maxLen);

    this.container = scene.add.container(0, 0).setDepth(120);

    const dim = scene.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0);
    const panelX = (NATIVE_WIDTH - PANEL_W) / 2;
    const panelY = (NATIVE_HEIGHT - PANEL_H) / 2;
    const panel = scene.add
      .rectangle(panelX, panelY, PANEL_W, PANEL_H, PALETTE.authorPanelBg, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(2, PALETTE.authorPanelEdge);
    const corner = scene.add.rectangle(panelX + 3, panelY + 3, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);
    const title = scene.add
      .text(NATIVE_WIDTH / 2, panelY + 12, opts.title, {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.hex(PALETTE.authorPanelEdge),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    const valueBox = scene.add
      .rectangle(NATIVE_WIDTH / 2, panelY + 30, PANEL_W - 32, 16, PALETTE.inventoryEmpty, 1)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.valueText = scene.add
      .text(panelX + 18, panelY + 30, "", {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.hex(PALETTE.bannerText),
      })
      .setOrigin(0, 0.5);

    this.container.add([dim, panel, corner, title, valueBox, this.valueText]);

    this.buildGrid(panelX, panelY + 46);
    this.repaintValue();
    this.repaintFocus();

    // Blinking caret.
    this.caretTimer = scene.time.addEvent({
      delay: 450,
      loop: true,
      callback: () => {
        this.caretVisible = !this.caretVisible;
        this.repaintValue();
      },
    });

    // Prime gamepad edge state from the *current* pad so the button press
    // that opened this keyboard (still held this frame) doesn't register as a
    // fresh press and immediately type / submit on the first tick.
    const pad = scene.input.gamepad?.pad1;
    if (pad) {
      this.prevButtons["A"] = pad.buttons[0]?.pressed === true;
      this.prevButtons["B"] = pad.buttons[1]?.pressed === true;
      this.prevButtons["Start"] = pad.buttons[9]?.pressed === true;
      const sx = pad.axes.length > 0 ? pad.axes[0]!.getValue() : 0;
      const sy = pad.axes.length > 1 ? pad.axes[1]!.getValue() : 0;
      this.prevDx = pad.left || sx < -0.5 ? -1 : pad.right || sx > 0.5 ? 1 : 0;
      this.prevDy = pad.up || sy < -0.5 ? -1 : pad.down || sy > 0.5 ? 1 : 0;
    }

    // Gamepad polling on the scene update loop.
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.tick, this);

    // Physical keyboard direct entry.
    this.keydownHandler = (ev: KeyboardEvent) => this.onKey(ev);
    scene.input.keyboard?.on("keydown", this.keydownHandler);
  }

  isOpen(): boolean {
    return this.open;
  }

  destroy(): void {
    this.open = false;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.tick, this);
    this.scene.input.keyboard?.off("keydown", this.keydownHandler);
    this.caretTimer?.remove();
    this.caretTimer = undefined;
    this.container.destroy();
  }

  // -- layout ---------------------------------------------------------------

  private buildGrid(originX: number, originY: number): void {
    for (const rowStr of CHAR_ROWS) {
      this.grid.push([...rowStr].map((c) => ({ label: c, kind: "char", value: c })));
    }
    this.grid.push([
      { label: "SPACE", kind: "space" },
      { label: "DEL", kind: "del" },
      { label: "OK", kind: "ok" },
    ]);

    const charRowWidth = 10 * (CELL + CELL_GAP) - CELL_GAP;
    const gridX = originX + (PANEL_W - charRowWidth) / 2;

    for (let r = 0; r < this.grid.length; r++) {
      const row = this.grid[r]!;
      const rowObjs: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
      const isActionRow = r === this.grid.length - 1;
      // Action row keys are wider so SPACE / DEL / OK read clearly.
      const actionW = (charRowWidth - 2 * CELL_GAP) / 3;
      for (let c = 0; c < row.length; c++) {
        const key = row[c]!;
        const w = isActionRow ? actionW : CELL;
        const x = isActionRow ? gridX + c * (actionW + CELL_GAP) : gridX + c * (CELL + CELL_GAP);
        const y = originY + r * (CELL + CELL_GAP);
        const bg = this.scene.add
          .rectangle(x, y, w, CELL, PALETTE.pauseButtonBg, 0.95)
          .setOrigin(0, 0)
          .setStrokeStyle(1, PALETTE.pauseButtonEdge)
          .setInteractive({ useHandCursor: true });
        const label = this.scene.add
          .text(x + w / 2, y + CELL / 2, key.label, {
            fontFamily: "monospace",
            fontSize: isActionRow ? "8px" : "10px",
            color: this.hex(PALETTE.hudText),
            fontStyle: "bold",
          })
          .setOrigin(0.5, 0.5);
        const rr = r;
        const cc = c;
        bg.on("pointerover", () => {
          this.focusRow = rr;
          this.focusCol = cc;
          this.repaintFocus();
        });
        bg.on("pointerdown", () => {
          this.focusRow = rr;
          this.focusCol = cc;
          this.activate();
        });
        this.container.add([bg, label]);
        rowObjs.push({ bg, label });
      }
      this.cellObjs.push(rowObjs);
    }
  }

  // -- input ----------------------------------------------------------------

  private tick(): void {
    const pad = this.scene.input.gamepad?.pad1;
    if (!pad) return;
    const stickX = pad.axes.length > 0 ? pad.axes[0]!.getValue() : 0;
    const stickY = pad.axes.length > 1 ? pad.axes[1]!.getValue() : 0;
    let dx = 0;
    let dy = 0;
    if (pad.left || stickX < -0.5) dx = -1;
    else if (pad.right || stickX > 0.5) dx = 1;
    if (pad.up || stickY < -0.5) dy = -1;
    else if (pad.down || stickY > 0.5) dy = 1;

    if (dx !== this.prevDx) {
      this.prevDx = dx;
      if (dx !== 0) this.moveFocus(dx, 0);
    }
    if (dy !== this.prevDy) {
      this.prevDy = dy;
      if (dy !== 0) this.moveFocus(0, dy);
    }

    if (this.padJustDown(pad.buttons[0], "A")) this.activate();
    if (this.padJustDown(pad.buttons[1], "B")) this.backspace();
    if (this.padJustDown(pad.buttons[9], "Start")) this.submit();
  }

  private padJustDown(button: Phaser.Input.Gamepad.Button | undefined, key: string): boolean {
    const was = this.prevButtons[key] === true;
    const is = button?.pressed === true;
    this.prevButtons[key] = is;
    return !was && is;
  }

  private onKey(ev: KeyboardEvent): void {
    const k = ev.key;
    if (k === "Backspace") {
      ev.preventDefault();
      this.backspace();
    } else if (k === "Enter") {
      ev.preventDefault();
      this.submit();
    } else if (k === "Escape") {
      ev.preventDefault();
      this.opts.onCancel();
      this.destroy();
    } else if (k === " ") {
      ev.preventDefault();
      this.append(" ");
    } else if (k.length === 1) {
      const ch = k.toLowerCase();
      if (ALLOWED_CHAR.test(ch)) this.append(ch);
    }
  }

  private moveFocus(dx: number, dy: number): void {
    if (dy !== 0) {
      this.focusRow = Phaser.Math.Clamp(this.focusRow + dy, 0, this.grid.length - 1);
    }
    const rowLen = this.grid[this.focusRow]!.length;
    if (dx !== 0) {
      this.focusCol = Phaser.Math.Clamp(this.focusCol + dx, 0, rowLen - 1);
    } else {
      // Row changed — keep the column in range.
      this.focusCol = Phaser.Math.Clamp(this.focusCol, 0, rowLen - 1);
    }
    this.repaintFocus();
  }

  private activate(): void {
    const key = this.grid[this.focusRow]?.[this.focusCol];
    if (!key) return;
    switch (key.kind) {
      case "char":
        this.append(key.value ?? "");
        break;
      case "space":
        this.append(" ");
        break;
      case "del":
        this.backspace();
        break;
      case "ok":
        this.submit();
        break;
    }
  }

  private append(ch: string): void {
    if (this.value.length >= this.maxLen) return;
    this.value += ch;
    this.repaintValue();
  }

  private backspace(): void {
    if (this.value.length === 0) return;
    this.value = this.value.slice(0, -1);
    this.repaintValue();
  }

  private submit(): void {
    const trimmed = this.value.trim();
    this.opts.onSubmit(trimmed);
    this.destroy();
  }

  // -- paint ----------------------------------------------------------------

  private repaintValue(): void {
    const caret = this.caretVisible ? "_" : " ";
    this.valueText.setText(this.value + caret);
  }

  private repaintFocus(): void {
    for (let r = 0; r < this.cellObjs.length; r++) {
      for (let c = 0; c < this.cellObjs[r]!.length; c++) {
        const cell = this.cellObjs[r]![c]!;
        const focused = r === this.focusRow && c === this.focusCol;
        if (focused) {
          cell.bg.setStrokeStyle(2, UGC_CYAN);
          cell.bg.setFillStyle(PALETTE.pauseButtonBg, 1);
        } else {
          cell.bg.setStrokeStyle(1, PALETTE.pauseButtonEdge);
          cell.bg.setFillStyle(PALETTE.pauseButtonBg, 0.95);
        }
      }
    }
  }

  private hex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }
}
